import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import LayoutWithChat from './LayoutWithChat';
import ResponsiveTable, { Column } from './ResponsiveTable';

interface Player {
  id: string;
  name: string;
  position: string;
  team: string;
}

interface DraftPick {
  id: string;
  year: number;
  round: number;
  description: string;
}

interface AnalysisCategory {
  name: string;
  winner: 'you' | 'them';
  reasoning: string;
}

interface TradeAnalysis {
  winner: 'you' | 'them';
  winnerDescription: string;
  summary: string;
  categories: AnalysisCategory[];
}

interface TradeAnalyzerProps {}

const TradeAnalyzer: React.FC<TradeAnalyzerProps> = () => {
  const [sendPlayers, setSendPlayers] = useState<Player[]>([
    { id: '1', name: "Ja'Marr Chase", position: 'WR', team: 'CIN' },
    { id: '2', name: 'Kyren Williams', position: 'RB', team: 'LAR' }
  ]);

  const [sendPicks, setSendPicks] = useState<DraftPick[]>([
    { id: '1', year: 2025, round: 1, description: '2025 Round 1 Pick' }
  ]);

  const [receivePlayers, setReceivePlayers] = useState<Player[]>([
    { id: '1', name: 'CeeDee Lamb', position: 'WR', team: 'DAL' }
  ]);

  const [receivePicks, setReceivePicks] = useState<DraftPick[]>([
    { id: '1', year: 2025, round: 2, description: '2025 Round 2 Pick' },
    { id: '2', year: 2025, round: 3, description: '2025 Round 3 Pick' }
  ]);

  const [analysis] = useState<TradeAnalysis>({
    winner: 'them',
    winnerDescription: 'CeeDee Lamb, 2025 Rd 2 & 3',
    summary: 'While Ja\'Marr Chase is a top-tier asset, acquiring CeeDee Lamb and two high-round picks provides more overall value and roster flexibility for the rest of your season.',
    categories: [
      {
        name: 'Value Today',
        winner: 'you',
        reasoning: 'Ja\'Marr Chase is currently the higher-scoring player with elite target share and red zone usage.'
      },
      {
        name: 'Rest of Season Value',
        winner: 'them',
        reasoning: 'CeeDee Lamb has a more favorable playoff schedule and the Cowboys\' improved offensive line gives him higher upside.'
      },
      {
        name: 'Injury Risk',
        winner: 'them',
        reasoning: 'Chase has a minor hamstring concern while Lamb has been remarkably durable throughout his career.'
      },
      {
        name: 'Future Assets',
        winner: 'them',
        reasoning: 'The 2nd and 3rd round picks provide significant value for next season\'s roster building.'
      },
      {
        name: 'Positional Depth',
        winner: 'them',
        reasoning: 'Adding Lamb while keeping your RB depth with Williams creates better roster balance.'
      }
    ]
  });



  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Player columns configuration
  const playerColumns: Column[] = [
    {
      key: 'name',
      label: 'Player',
      width: '40%',
      className: 'font-medium text-text-primary',
      render: (value, player) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium text-text-secondary">
            {player.avatar ? (
              <img 
                src={player.avatar} 
                alt={player.name} 
                className="h-full w-full rounded-full object-cover"
              />
            ) : (
              getInitials(player.name)
            )}
          </div>
          <span>{value}</span>
        </div>
      )
    },
    {
      key: 'position',
      label: 'Position',
      width: '20%',
      className: 'text-text-secondary',
      render: (value) => value
    },
    {
      key: 'team',
      label: 'Team',
      width: '20%',
      className: 'text-text-secondary',
      render: (value) => value
    },
    {
      key: 'actions',
      label: ' ',
      width: '20%',
      className: '',
      render: (value, player) => (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            removePlayer(player.type, player.id);
          }}
          className="text-text-secondary hover:text-text-primary transition-colors"
        >
          <X size={20} />
        </button>
      )
    }
  ];

  // Draft picks columns configuration
  const pickColumns: Column[] = [
    {
      key: 'description',
      label: 'Pick',
      width: '80%',
      className: 'font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'actions',
      label: ' ',
      width: '20%',
      className: '',
      render: (value, pick) => (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            removePick(pick.type, pick.id);
          }}
          className="text-text-secondary hover:text-text-primary transition-colors"
        >
          <X size={20} />
        </button>
      )
    }
  ];

  // Analysis columns configuration
  const analysisColumns: Column[] = [
    {
      key: 'name',
      label: 'Category',
      width: '25%',
      className: 'text-sm font-medium text-text-primary',
      render: (value) => value
    },
    {
      key: 'winner',
      label: 'Winner',
      width: '15%',
      className: 'text-center',
      render: (value) => (
        <span className={`text-sm font-medium ${
          value === 'you' ? 'text-green-400' : 'text-red-400'
        }`}>
          {value === 'you' ? 'You win' : 'They win'}
        </span>
      )
    },
    {
      key: 'reasoning',
      label: 'Analysis',
      width: '60%',
      className: 'text-sm text-text-secondary',
      render: (value) => value
    }
  ];

  const removePlayer = (type: 'send' | 'receive', playerId: string) => {
    if (type === 'send') {
      setSendPlayers(sendPlayers.filter(p => p.id !== playerId));
    } else {
      setReceivePlayers(receivePlayers.filter(p => p.id !== playerId));
    }
  };

  const removePick = (type: 'send' | 'receive', pickId: string) => {
    if (type === 'send') {
      setSendPicks(sendPicks.filter(p => p.id !== pickId));
    } else {
      setReceivePicks(receivePicks.filter(p => p.id !== pickId));
    }
  };

  const addPlayer = (type: 'send' | 'receive') => {
    // Placeholder function for adding players
    console.log(`Add player to ${type}`);
  };

  const addPick = (type: 'send' | 'receive') => {
    // Placeholder function for adding draft picks
    console.log(`Add draft pick to ${type}`);
  };


  const AddButton: React.FC<{ 
    onClick: () => void; 
    children: React.ReactNode; 
  }> = ({ onClick, children }) => (
    <button
      onClick={onClick}
      className="w-full mt-2 text-accent-primary hover:text-blue-300 py-2 rounded-lg flex items-center justify-center gap-2 border-2 border-dashed border-border-primary hover:border-text-secondary transition-colors"
    >
      <Plus size={20} />
      <span>{children}</span>
    </button>
  );

  const TradeSide: React.FC<{
    title: string;
    players: Player[];
    picks: DraftPick[];
    type: 'send' | 'receive';
  }> = ({ title, players, picks, type }) => {
    // Add type to data for the remove buttons
    const playersWithType = players.map(player => ({ ...player, type }));
    const picksWithType = picks.map(pick => ({ ...pick, type }));

    return (
      <div className="bg-bg-secondary rounded-lg p-6">
        <h2 className="text-xl font-normal font-primary text-text-primary mb-4">{title}</h2>
        
        {/* Players Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-normal font-primary text-text-secondary">Players</h3>
          <ResponsiveTable
            columns={playerColumns}
            data={playersWithType}
            keyExtractor={(player) => player.id}
            cardRenderer={(player) => (
              <div className="bg-bg-tertiary border border-border-primary rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bg-primary text-xs font-medium text-text-secondary">
                      {player.avatar ? (
                        <img 
                          src={player.avatar} 
                          alt={player.name} 
                          className="h-full w-full rounded-full object-cover"
                        />
                      ) : (
                        getInitials(player.name)
                      )}
                    </div>
                    <div>
                      <h3 className="text-base font-medium text-text-primary">
                        {player.name}
                      </h3>
                      <div className="text-xs text-text-secondary">
                        {player.position} • {player.team}
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => removePlayer(type, player.id)}
                    className="text-text-secondary hover:text-text-primary transition-colors p-1"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            )}
          />
          <AddButton onClick={() => addPlayer(type)}>
            Add Player
          </AddButton>
        </div>

        {/* Draft Picks Section */}
        <div className="space-y-4 mt-6">
          <h3 className="text-lg font-normal font-primary text-text-secondary">Draft Picks</h3>
          <ResponsiveTable
            columns={pickColumns}
            data={picksWithType}
            keyExtractor={(pick) => pick.id}
            cardRenderer={(pick) => (
              <div className="bg-bg-tertiary border border-border-primary rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-medium text-text-primary">
                    {pick.description}
                  </h3>
                  <button 
                    onClick={() => removePick(type, pick.id)}
                    className="text-text-secondary hover:text-text-primary transition-colors p-1"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            )}
          />
          <AddButton onClick={() => addPick(type)}>
            Add Draft Pick
          </AddButton>
        </div>
      </div>
    );
  };

  return (
    <LayoutWithChat>
            {/* Page Title */}
            <h1 className="text-3xl font-normal font-primary text-text-primary">
              Trade Analyzer
            </h1>

            {/* Trade Sides */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TradeSide
                title="You Send"
                players={sendPlayers}
                picks={sendPicks}
                type="send"
              />
              <TradeSide
                title="You Receive"
                players={receivePlayers}
                picks={receivePicks}
                type="receive"
              />
            </div>

            {/* Trade Analysis */}
            <div className="bg-bg-secondary rounded-lg p-6">
              <h2 className="text-xl font-normal font-primary text-text-primary mb-6">
                Trade Analysis
              </h2>
              
              {/* Overall Winner */}
              <div className="flex items-center justify-center p-6 bg-bg-tertiary rounded-lg mb-6">
                <div className="text-center">
                  <p className="text-lg text-text-secondary mb-2">
                    {analysis.winner === 'you' ? 'You win' : 'They win'} this trade
                  </p>
                  <p className="text-2xl font-medium text-green-400 mb-4">
                    {analysis.winnerDescription}
                  </p>
                  <p className="text-text-secondary max-w-xl mx-auto">
                    {analysis.summary}
                  </p>
                </div>
              </div>

              {/* Category Breakdown */}
              <div className="space-y-4">
                <h3 className="text-lg font-normal font-primary text-text-primary mb-4">
                  Detailed Analysis
                </h3>
                <ResponsiveTable
                  columns={analysisColumns}
                  data={analysis.categories}
                  keyExtractor={(category) => category.name}
                  cardRenderer={(category) => (
                    <div className="bg-bg-tertiary border border-border-primary rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-base font-medium text-text-primary">
                          {category.name}
                        </h3>
                        <span className={`text-sm font-medium px-2 py-1 rounded ${
                          category.winner === 'you' ? 'text-green-400 bg-green-400/20' : 'text-red-400 bg-red-400/20'
                        }`}>
                          {category.winner === 'you' ? 'You win' : 'They win'}
                        </span>
                      </div>
                      <p className="text-sm text-text-secondary">
                        {category.reasoning}
                      </p>
                    </div>
                  )}
                />
              </div>
            </div>
    </LayoutWithChat>
  );
};

export default TradeAnalyzer;